/**
 * formulaWorkerBridge.js
 * Promise-based bridge to formula.worker.js
 * Manages worker lifecycle, request queuing, and message routing.
 *
 * Usage:
 *   import formulaWorkerBridge from './formulaWorkerBridge';
 *   await formulaWorkerBridge.init(sheetsMeta, dataMap);
 *   const changes = await formulaWorkerBridge.setCell(0, 2, 1, '=SUM(A1:A10)');
 */

class FormulaWorkerBridge {
  constructor() {
    this._worker    = null;
    this._pending   = new Map(); // requestId → { resolve, reject }
    this._listeners = new Map(); // event type → Set<callback>
    this._reqId     = 0;
    this._ready     = false;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  start() {
    if (this._worker) return;

    this._worker = new Worker(
      new URL('./formula.worker.js', import.meta.url),
      { type: 'module' }
    );

    this._worker.onmessage = (e) => this._handleMessage(e.data);
    this._worker.onerror   = (e) => {
      console.error('[FormulaWorkerBridge] Worker error:', e);
      // Reject all pending
      this._pending.forEach(({ reject }) => reject(new Error(e.message)));
      this._pending.clear();
    };
  }

  terminate() {
    if (this._worker) {
      this._worker.terminate();
      this._worker  = null;
      this._ready   = false;
      this._pending.clear();
    }
  }

  // ── Internal message handler ───────────────────────────────────────────────

  _handleMessage(data) {
    const { type, requestId, payload } = data;

    if (type === 'CHANGED') {
      // Broadcast to all CHANGED listeners (e.g. grid refresh)
      this._emit('CHANGED', payload);
      // Also resolve the pending request if any
      if (requestId && this._pending.has(requestId)) {
        const { resolve } = this._pending.get(requestId);
        this._pending.delete(requestId);
        resolve(payload);
      }
      return;
    }

    if (!requestId || !this._pending.has(requestId)) return;

    const { resolve, reject } = this._pending.get(requestId);
    this._pending.delete(requestId);

    if (type === 'ERROR') {
      reject(new Error(payload?.message || 'Worker error'));
    } else {
      resolve(payload);
    }
  }

  // ── Event emitter ──────────────────────────────────────────────────────────

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
    return () => this._listeners.get(event)?.delete(callback);
  }

  _emit(event, data) {
    this._listeners.get(event)?.forEach((cb) => cb(data));
  }

  // ── Request sender ─────────────────────────────────────────────────────────

  _send(type, payload) {
    if (!this._worker) this.start();

    const requestId = `req_${++this._reqId}`;
    return new Promise((resolve, reject) => {
      this._pending.set(requestId, { resolve, reject });
      this._worker.postMessage({ type, payload, requestId });

      // Timeout safety (30s)
      setTimeout(() => {
        if (this._pending.has(requestId)) {
          this._pending.delete(requestId);
          reject(new Error(`Worker request timed out: ${type}`));
        }
      }, 30000);
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Initialize HyperFormula with sheet metadata and row data.
   * @param {Array}  sheetsMeta  - [{ sheetId, sheetName, columns }]
   * @param {Object} dataMap     - { sheetId: rowDataArray }
   * @param {Object} config      - optional HyperFormula config overrides
   */
  init(sheetsMeta, dataMap, config = {}) {
    this.start();
    return this._send('INIT', { sheetsMeta, dataMap, hyperformulaConfig: config });
  }

  /**
   * Set a single cell value/formula and get back changed cells.
   * @param {number} sheetIndex
   * @param {number} row         zero-based
   * @param {number} col         zero-based
   * @param {*}      value       raw value or "=FORMULA"
   */
  setCell(sheetIndex, row, col, value) {
    return this._send('SET_CELL', { sheetIndex, row, col, value });
  }

  /**
   * Batch-set multiple cells at once.
   * @param {number} sheetIndex
   * @param {Array}  cells       [{ row, col, value }]
   */
  setCells(sheetIndex, cells) {
    return this._send('SET_CELLS', { sheetIndex, cells });
  }

  /**
   * Get a single cell's computed value and formula.
   */
  getCell(sheetIndex, row, col) {
    return this._send('GET_CELL', { sheetIndex, row, col });
  }

  /**
   * Get the full computed values of a sheet (2D array).
   */
  getSheet(sheetIndex) {
    return this._send('GET_SHEET', { sheetIndex });
  }

  /**
   * Add a new sheet and return its ID.
   */
  addSheet(sheetName) {
    return this._send('ADD_SHEET', { sheetName });
  }

  /**
   * Validate a formula string.
   */
  validateFormula(formula) {
    return this._send('VALIDATE_FORMULA', { formula });
  }

  /**
   * Force full recalculation of all sheets.
   */
  recalc() {
    return this._send('RECALC', {});
  }
}

// Singleton — one bridge shared across the spreadsheet platform
const formulaWorkerBridge = new FormulaWorkerBridge();
export default formulaWorkerBridge;
